import { LitElement, PropertyValues, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { storeToRefs } from "pinia";
import { watch } from "vue";
import type { RouteLocationRaw } from "vue-router";

import { useLayerVisibility } from "@/composables/bpmn-manager/layer";
import { Domain, useDomainManager } from "@/composables/domain-manager";
import { useEditManager } from "@/composables/edit-manager";
import { useOrganizationStore } from "@/composables/organization-manager";
import { useProcessManager } from "@/composables/process-manager";
import { Product, useProductManager } from "@/composables/product-manager";
import { useStekManager } from "@/composables/stek-manager";
import {
    type Worktree,
    useWorktreeManager,
    useWorktreeStore,
} from "@/composables/worktree-manager";
import { positionContextMenu } from "@/lib/context-menu-position";
import { diffStatusClasses } from "@/lib/diff-status";
import { withThreadRevision } from "@/lib/thread-revision-link";
import router from "@/router";
import { type DomainInTree, useDomainsStore } from "@/stores/domains-store";
import { EntityType } from "@/stores/entity-store";
import {
    Process,
    type ProcessInTree,
    useProcessesStore,
} from "@/stores/processes-store";
import { type ProductInTree, useProductsStore } from "@/stores/products-store";
import { useReleaseStore } from "@/stores/release-store";
import { useThreadsStore } from "@/stores/threads-store";
import { useTriageStore } from "@/stores/triage-store";
import { useUserStore } from "@/stores/user-store";

import {
    domainMenuFields,
    processMenuFields,
    productMenuFields,
} from "./menu-fields";

const layerVisibility = useLayerVisibility();

interface BaseMenuState {
    creating?: boolean;
    open: boolean;
    selected: boolean;
    renaming: boolean;
    newName?: string;

    // When 'placeholder' is true, the domain doesn't actually exist yet.
    // This is used as a transitionary state when creating a new domain,
    // to allow the UI to show a domain placeholder during creation.
    placeholder?: boolean;

    expandTimeoutId?: ReturnType<typeof setTimeout>;
    dropTarget?: boolean;
}

interface DomainMenuState extends BaseMenuState {
    newProductName?: string;
}

interface ProductMenuState extends BaseMenuState {
    newProcessName?: string;
}

// The same interfaces as worktree.DomainInTree, but with a little state
// information sprinkled on top. These interfaces are specifically meant
// for this component.
interface DomainInMenu extends DomainInTree {
    state: DomainMenuState;
    products: ProductInMenu[];
}

interface ProductInMenu extends ProductInTree {
    domain: DomainInMenu;
    state: ProductMenuState;
    processes: ProcessInMenu[];
}

interface ProcessInMenu extends ProcessInTree {
    product: ProductInMenu;
    state: BaseMenuState;
}

interface DragInfo {
    entity: DomainInMenu | ProductInMenu | ProcessInMenu;
    dropTarget?: DomainInMenu | ProductInMenu | ProcessInMenu;
    reorderPosition?: "before" | "after";
    dropSuccess: boolean;
}

// A special type which has no parents or state. It is used to communicate
// that a new domain has to be created.
interface NewDomain {
    name: string;
}

function isDomainInMenu(
    entity: DomainInMenu | ProductInMenu | ProcessInMenu | NewDomain
): entity is DomainInMenu {
    return entity && "products" in entity;
}

function isProductInMenu(
    entity: DomainInMenu | ProductInMenu | ProcessInMenu | NewDomain
): entity is ProductInMenu {
    return entity && "processes" in entity;
}

function isProcessInMenu(
    entity: DomainInMenu | ProductInMenu | ProcessInMenu | NewDomain
): entity is ProcessInMenu {
    return entity && "product" in entity;
}

function getDefaultMenuState() {
    return {
        open: false,
        selected: false,
        renaming: false,
        creating: false,
    };
}

@customElement("c-stekz-tree")
export class StekzTreeComponent extends LitElement {
    manager = useWorktreeManager();
    store = useWorktreeStore();
    isEditing = useEditManager().isEditing;
    threadsStore = useThreadsStore();
    triageStore = useTriageStore();
    releaseStore = useReleaseStore();
    userStore = useUserStore();

    constructor() {
        super();

        watch(
            this.manager.worktree,
            () => {
                this.loading = true;
                this.updateMenu();
            },
            { immediate: true }
        );

        // Re-render discussion badges when the open-thread set changes or the
        // viewing context flips (badges hide while viewing a thread branch).
        const { entityThreadMap } = storeToRefs(this.threadsStore);
        watch(
            [
                entityThreadMap,
                () => this.releaseStore.activeRevision,
                () => this.userStore.threadsEnabled,
            ],
            () => this.requestUpdate()
        );

        // Re-render improvement badges when the open-issue index or the
        // Improvements layer toggle changes.
        const { entityIssueMap, entityRollupCounts } = storeToRefs(
            this.triageStore
        );
        watch(
            [
                entityIssueMap,
                entityRollupCounts,
                () => layerVisibility.value.triage,
                () => this.releaseStore.activeRevision,
                () => this.userStore.externalCommunicationEnabled,
            ],
            () => this.requestUpdate()
        );

        watch(
            this.isEditing,
            () => {
                this.updateMenu();
            },
            { immediate: true }
        );
    }

    static styles = css`
        :host {
            --ul-indent: 24px;
            --ul-icon-size: 24px;
            --expand-icon-size: 20px;
        }
        input {
            font-size: 1em;
            padding: 6px 16px;
            z-index: 2200;
            width: 100%;
            box-sizing: border-box;
        }
        .file-list.root {
            margin-left: 0;
            padding-left: 0;
            margin-block-start: 0;
            margin-block-end: 0;
        }

        .file-list,
        .file-list ul {
            list-style-type: none;
            font-size: 1em;
            color: var(--text-color);
            line-height: 1.5em;
            margin-left: -17px;
            padding-left: var(--ul-indent);
            border-left: 1px dotted #aaa;
        }
        .file-list li {
            position: relative;
        }

        li.node-root ul {
            transition: all 0.2s ease-in-out;
            padding-left: 0;
            margin-left: var(--ul-indent);
        }

        li.node-root.new {
            margin-bottom: 3px;
        }

        li.node-root.closed > ul {
            opacity: 0;
            max-height: 0;
        }
        li.node-root.open > ul {
            opacity: 1;
            display: block;
        }

        div.new-entity {
            color: #aaa;
            cursor: text;
        }

        li a {
            text-decoration: none;
            color: var(--text-color);
        }

        /* When dragging an item, we want to hide it from the list */
        li.dragging {
            transition: 0.01s;
            transform: translateX(-9999px);
        }

        .file-list li.draggable .entity-title:hover {
            position: relative;
            z-index: 1;
        }

        .file-list li.draggable .entity-title:hover:after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 1;
            border: 2px solid var(--stekz-primary-color);
            pointer-events: none;
        }

        .drop-target > .entity-title {
            outline: 2px solid var(--stekz-primary-color);
            background: var(--stekz-primary-background);
        }

        .drop-before:before {
            content: "";
            position: absolute;
            top: 0;
            left: var(--ul-indent);
            right: 0;
            height: 2px;
            background: var(--stekz-primary-color);
            z-index: 10;
        }

        .drop-after:after {
            content: "";
            position: absolute;
            bottom: 0;
            left: var(--ul-indent);
            right: 0;
            height: 2px;
            background: var(--stekz-primary-color);
            z-index: 10;
        }

        /* Domains are siblings in the root list, so their reorder marker must
         * start at that level rather than at the nested-entity indent. */
        .domain-root.drop-before:before,
        .domain-root.drop-after:after {
            left: 0;
        }

        .file-list li .entity-name {
            display: block;
            position: relative;
            padding: 8px;
            padding-left: calc(var(--ul-icon-size) + var(--ul-indent));
            margin-left: var(--expand-icon-size);
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .file-list li.selected > .entity-title {
            background: var(--surface-background);
        }
        .file-list li .entity-title:hover {
            background: var(--stekz-primary-background);
        }

        .file-list li .entity-name:before {
            display: block;
            position: absolute;
            top: 10px;
            left: calc(var(--ul-indent) / 2);
            width: var(--ul-icon-size);
            height: var(--ul-icon-size);
            mask-size: cover;
            /* background-color fills the svgs */
            background-color: var(--text-color);
            content: "";
        }

        .file-list li.new .entity-name:before {
            background-color: #aaa;
            mask-image: url("/assets/primeicons/plus.svg") !important;
        }

        .file-list li.selected > .entity-title > .entity-name,
        .file-list li > .entity-title:hover > .entity-name {
            font-weight: bold;
        }

        .file-list li .expand-icon:not(.empty) {
            display: block;
            position: absolute;
            top: 10px;
            width: var(--expand-icon-size);
            height: var(--expand-icon-size);
            mask-size: cover;
            mask-repeat: no-repeat;
            background-color: var(--text-color);
            content: "";
        }

        .file-list li.node-root.closed > .entity-title > .expand-icon {
            mask-image: url("/assets/primeicons/angle-right.svg");
        }

        .file-list li.node-root.open > .entity-title > .expand-icon {
            mask-image: url("/assets/primeicons/angle-down.svg");
        }

        .file-list .domain-root .entity-name:before {
            mask-image: url("/assets/primeicons/cloud.svg");
        }
        .file-list .product-root .entity-name:before {
            mask-image: url("/assets/primeicons/box.svg");
        }
        .file-list .process-root .entity-name:before {
            mask-image: url("/assets/primeicons/share-alt.svg");
        }

        .file-list .node-root .placeholder {
            color: #aaa;
            cursor: unset;
        }

        /* Thread diff indicators (when viewing a thread branch). Applied to the
           entity <li> via diff_status; only colour the entity's own title row. */
        .file-list li.diff-added > .entity-title > .entity-name {
            color: var(--stekz-diff-added-color, #16a34a);
        }
        .file-list li.diff-modified > .entity-title > .entity-name {
            color: var(--stekz-diff-modified-color, #d97706);
        }
        .file-list li.diff-removed > .entity-title > .entity-name {
            text-decoration: line-through;
            opacity: 0.6;
        }

        /* Discussion badge — an entity with an open thread. Sits at the right of
           the title row; click opens the thread drawer. */
        .file-list li .entity-title > .thread-badge {
            position: absolute;
            right: 8px;
            top: 9px;
            display: inline-flex;
            align-items: center;
            gap: 2px;
            cursor: pointer;
            z-index: 5;
        }
        .file-list li .entity-title > .thread-badge:hover {
            opacity: 0.7;
        }
        .file-list li .thread-badge-icon {
            display: block;
            width: 15px;
            height: 15px;
            mask-image: url("/assets/primeicons/comments.svg");
            mask-size: cover;
            mask-repeat: no-repeat;
            background-color: var(--stekz-primary-color, #6366f1);
        }
        .file-list li .thread-badge-count {
            font-size: 0.7rem;
            line-height: 1;
            color: var(--stekz-primary-color, #6366f1);
        }

        /* Improvement badge — an entity (or subtree) with open triage issues,
           shown while the Improvements layer is on. Click opens the triage
           page scoped to the entity. Roll-ups (domain/product aggregates)
           render muted so the direct process badges carry the emphasis. */
        .file-list li .entity-title > .triage-badge {
            position: absolute;
            right: 8px;
            top: 9px;
            display: inline-flex;
            align-items: center;
            gap: 2px;
            cursor: pointer;
            z-index: 5;
        }
        .file-list li .entity-title > .thread-badge ~ .triage-badge {
            right: 36px;
        }
        .file-list li .entity-title > .triage-badge:hover {
            opacity: 0.7;
        }
        .file-list li .triage-badge-icon {
            display: block;
            width: 14px;
            height: 14px;
            mask-image: url("/assets/primeicons/sparkles.svg");
            mask-size: cover;
            mask-repeat: no-repeat;
            background-color: var(--stekz-triage-color, #c9930a);
        }
        .file-list li .triage-badge-count {
            font-size: 0.7rem;
            line-height: 1;
            color: var(--stekz-triage-color, #c9930a);
        }
        .file-list li .entity-title > .triage-badge.rollup {
            opacity: 0.55;
        }
        .file-list li .triage-badge.rollup .triage-badge-icon {
            width: 12px;
            height: 12px;
        }

        .context-menu-bg {
            position: fixed;
            inset: 0;
            z-index: 100;
        }

        .context-menu-bg > menu {
            background: var(--stekz-primary-background);
            color: var(--text-color);
            box-shadow: rgba(0, 0, 0, 0.25) 2px 2px 8px;
            list-style: none;
            margin: 0;
            padding: 0;
            min-width: 160px;
        }
        .context-menu-bg > menu > li {
            padding: 8px 16px;
            cursor: pointer;
        }

        .context-menu-bg > menu > li:hover {
            color: var(--text-color-primary-contrast);
            background: var(--stekz-primary-color);
        }
    `;

    @property({ attribute: "data-selected-domain-id" })
    selectedDomainId: string = "";

    @property({ attribute: "data-selected-product-id" })
    selectedProductId: string = "";

    @property({ attribute: "data-selected-process-id" })
    selectedProcessId: string = "";

    @property({ attribute: "data-stek-id" })
    stekId: string = "";

    // The mode of the tree. In "live" mode, all editing affordances
    // (drag/drop, context menus, "New X" entries, rename) are hidden
    // and entity links route to the live mode pages.
    @property({ attribute: "data-mode" })
    mode: "design" | "live" | "dev" = "design";

    // Effective editing state: editing is only enabled in design mode.
    // Guards against transient cases where isEditing remains true after
    // switching from design to live mode.
    get editing() {
        return this.isEditing.value && this.mode !== "live";
    }

    @state()
    dragInfo: DragInfo | null = null;

    @state()
    domains: DomainInMenu[] = [];

    // The loading icon is only shown while the initial tree is being loaded,
    // after this the tree will be updated async without showing the loading
    // icon, unless you switch the stek.
    @state()
    loading = true;

    @state()
    contextMenu: {
        // Points to the entity that we open a context menu for.
        entity: DomainInMenu | ProductInMenu | ProcessInMenu;
        x: number;
        y: number;
    } | null = null;

    @state()
    creatingDomain = false;

    @state()
    newDomainName: string = "";

    deselectAll() {
        this.domains.forEach((domain) => {
            domain.state.selected = false;
            domain.state.open = false;
            domain.products.forEach((product) => {
                product.state.selected = false;
                product.state.open = false;
                product.processes.forEach((process) => {
                    process.state.selected = false;
                    process.state.open = false;
                });
            });
        });
    }

    // Deselect all entities that do not have the given identity.
    deselectOthers(entityId: string) {
        this.domains.forEach((domain) => {
            if (domain.identity !== entityId) {
                domain.state.selected = false;
            }
            domain.products.forEach((product) => {
                if (product.identity !== entityId) {
                    product.state.selected = false;
                }
                product.processes.forEach((process) => {
                    if (process.identity !== entityId) {
                        process.state.selected = false;
                    }
                });
            });
        });
    }

    willUpdate(changedProperties: PropertyValues<this>) {
        super.willUpdate(changedProperties);

        // Only update the tree when the stekId updates, avoid updating the tree
        // when the component renders for the first time.
        if (changedProperties.has("stekId") && this.stekId) {
            this.placeholderEntities = { [this.stekId]: [] };
        }

        // Whenever the selected domain, product or process changes, update
        // the state in the tree to reflect the new selection.
        if (
            changedProperties.has("selectedDomainId") ||
            changedProperties.has("selectedProductId") ||
            changedProperties.has("selectedProcessId")
        ) {
            this.select(
                this.selectedDomainId,
                this.selectedProductId,
                this.selectedProcessId
            );
            this.requestUpdate();
        }
    }

    // Either returns a default state, or if the entity was already in the
    // tree, returns the state for that entity from the previous tree.
    getStateForEntity(
        entity: Omit<DomainInMenu | ProductInMenu | ProcessInMenu, "state">,
        oldEntity?: DomainInMenu | ProductInMenu | ProcessInMenu
    ) {
        const defaultState = getDefaultMenuState();

        // From a process node, you can never be 'creating' a child node.
        if (isProcessInMenu(entity)) {
            defaultState.creating = false;
        }

        if (!oldEntity) {
            return defaultState;
        }

        // Override the default state with the state from the previous tree,
        // this way, the state of the tree is preserved when the tree is
        // updated.
        return {
            ...defaultState,
            ...oldEntity.state,
        };
    }

    async updateMenu() {
        const domains: DomainInMenu[] = [];
        if (!this.manager.worktree.value) {
            return;
        }
        // Copy the worktree and enrich it with some state information.
        this.manager.worktree.value.domains.forEach((domain: DomainInTree) => {
            const baseDomainInMenu: Omit<DomainInMenu, "state"> = {
                ...domainMenuFields(domain),
                products: [],
            };
            const oldDomainInMenu = this.domains.find(
                (d) => d.identity === domain.identity
            );

            const domainInMenu: DomainInMenu = {
                ...baseDomainInMenu,
                state: this.getStateForEntity(
                    baseDomainInMenu,
                    oldDomainInMenu
                ),
            };

            domain.products.forEach((product: ProductInTree) => {
                const baseProductInMenu: Omit<ProductInMenu, "state"> = {
                    domain: domainInMenu,
                    ...productMenuFields(product),
                    processes: [],
                };
                const oldProductInMenu = oldDomainInMenu?.products.find(
                    (p) => p.identity === product.identity
                );

                const productInMenu: ProductInMenu = {
                    ...baseProductInMenu,
                    state: this.getStateForEntity(
                        baseProductInMenu,
                        oldProductInMenu
                    ),
                };

                product.processes.forEach((process: ProcessInTree) => {
                    const baseProcessInMenu: Omit<ProcessInMenu, "state"> = {
                        product: productInMenu,
                        ...processMenuFields(process),
                    };
                    const oldProcessInMenu = oldProductInMenu?.processes.find(
                        (p) => p.identity === process.identity
                    );
                    const processInMenu: ProcessInMenu = {
                        ...baseProcessInMenu,
                        state: this.getStateForEntity(
                            baseProcessInMenu,
                            oldProcessInMenu
                        ),
                    };
                    productInMenu.processes.push(processInMenu);
                });

                domainInMenu.products.push(productInMenu);
            });

            domains.push(domainInMenu);
        });
        const wasEmpty = this.domains.length === 0;
        this.domains = domains;

        // Only update selection state when an entity is actually selected (non-entity
        // pages pass empty IDs, and calling select() would collapse the entire tree
        // via deselectAll()). For initial load (wasEmpty), always call select() to
        // open the path to the selected entity from default-closed state.
        if (
            wasEmpty ||
            this.selectedDomainId ||
            this.selectedProductId ||
            this.selectedProcessId
        ) {
            this.select(
                this.selectedDomainId,
                this.selectedProductId,
                this.selectedProcessId
            );
        }
        this.loading = false;
        this.requestUpdate();
    }

    async refreshTree() {
        // Sync the worktree store and rebuild the component's mirrored menu
        // before returning. Callers that need to find/navigate to a newly
        // created entity must not race the separate worktree watcher.
        await this.store.sync();
        await this.updateMenu();
    }

    createProcessTemplate(_domain: DomainInMenu, product: ProductInMenu) {
        if (product.state.creating) {
            return html` <li class="node-root process-root new new-input">
                <div class="entity-name">
                    <input
                        placeholder="Name your process"
                        type="text"
                        value=${product.state.newProcessName}
                        @blur=${() => {
                            product.state!.creating = false;
                            this.requestUpdate();
                        }}
                        @keydown=${this.onKeydownForCreationEvent(product)}
                        @input=${(e: InputEvent) => {
                            product.state!.newProcessName = (
                                e.target as HTMLInputElement
                            ).value;
                        }}
                    />
                </div>
            </li>`;
        }

        return html`<li class="node-root process-root new">
            <div
                class="entity-name new-entity"
                @click=${async (e: MouseEvent) => {
                    e.stopPropagation();
                    product.state.creating = true;

                    this.requestUpdate();

                    await this.updateComplete;

                    const inputField = this.renderRoot.querySelector("input");
                    inputField?.focus();
                }}
            >
                New process
            </div>
        </li>`;
    }
    onKeydownForCreationEvent(
        entity: DomainInMenu | ProductInMenu | NewDomain
    ) {
        return (e: KeyboardEvent) => {
            // Some special logic in case the user is creating a new domain.
            // (i.e. not a child entity of a domain or product)
            if (!isDomainInMenu(entity) && !isProductInMenu(entity)) {
                if (e.key === "Escape") {
                    this.creatingDomain = false;
                    this.newDomainName = "";
                    this.requestUpdate();
                } else if (e.key === "Enter") {
                    this.creatingDomain = false;
                    this.createEntity({
                        name: this.newDomainName,
                    });
                    this.newDomainName = "";
                }
                return;
            }

            if (e.key === "Escape") {
                entity.state.creating = false;

                if (isDomainInMenu(entity)) {
                    this.newDomainName = "";
                } else if (isProductInMenu(entity)) {
                    entity.state.newProcessName = "";
                }

                this.requestUpdate();
            } else if (e.key === "Enter") {
                entity.state.creating = false;
                this.requestUpdate();
                // Be sure to copy the object, as the reference
                // to the object might be lost if the tree gets
                // updated in the meantime
                this.createEntity({
                    ...entity,
                    state: { ...entity.state },
                });

                if (isDomainInMenu(entity)) {
                    entity.state.newProductName = "";
                } else if (isProductInMenu(entity)) {
                    entity.state.newProcessName = "";
                }
            }
        };
    }

    createProductTemplate(domain: DomainInMenu) {
        if (domain.state.creating) {
            return html` <li
                class="node-root product-root closed new new-input"
            >
                <div class="entity-name">
                    <input
                        placeholder="Name your product / service"
                        type="text"
                        value=${domain.state.newProductName}
                        @blur=${() => {
                            domain.state.creating = false;
                            this.requestUpdate();
                        }}
                        @keydown=${this.onKeydownForCreationEvent(domain)}
                        @input=${(e: InputEvent) => {
                            domain.state.newProductName = (
                                e.target as HTMLInputElement
                            ).value;
                        }}
                    />
                </div>
            </li>`;
        }

        return html`<li class="node-root product-root closed new">
            <div
                class="entity-name new-entity"
                @click=${async (e: MouseEvent) => {
                    e.stopPropagation();
                    domain.state.creating = true;

                    this.requestUpdate();

                    await this.updateComplete;

                    const inputField = this.renderRoot.querySelector("input");
                    inputField?.focus();
                }}
            >
                New product
            </div>
        </li>`;
    }

    @state()
    placeholderEntities: {
        [parentNanoid: string]: (
            DomainInMenu | ProductInMenu | ProcessInMenu
        )[];
    } = {};

    // Returns a function that can be called to remove a specific placeholder
    // entity from the tree.
    removePlaceholderEntityFn(
        parentEntity: DomainInMenu | ProductInMenu | ProcessInMenu | NewDomain,
        name: string
    ) {
        // When this function is called, we need to check whether it was called
        // or the creation of the entity we're removing the placeholder for,
        // as it could also be called for the creation of different entities.
        return (newTree: Worktree) => {
            // Flatten the entire tree of entities and get an object of parent nanoids
            // and the names of all children for each of those nanoids.
            const allEntities: Record<string, string[]> = {};
            allEntities[this.stekId] ??= [];
            newTree.domains.forEach((domain) => {
                allEntities[domain.identity] ??= [];
                allEntities[this.stekId].push(domain.name);
                domain.products.forEach((product) => {
                    allEntities[domain.identity].push(product.name);
                    allEntities[product.identity] ??= [];
                    product.processes.forEach((process) => {
                        allEntities[product.identity].push(process.name);
                    });
                });
            });

            // The 'NewDomain' entity is somewhat of a special case, as it has no parent entity, for this
            // we can use the stekId as the parent identity.
            const parentIdentity =
                "identity" in parentEntity
                    ? parentEntity.identity
                    : this.stekId;
            if (!(parentIdentity in allEntities)) {
                return;
            }

            // Only remove the name when it exists in the new tree.
            if (!allEntities[parentIdentity].includes(name)) {
                return;
            }

            this.removePlaceholderEntity(parentIdentity, name);
        };
    }

    removePlaceholderEntity(parentIdentity: string, name: string) {
        const placeHolderIndex = this.placeholderEntities[
            parentIdentity
        ].findIndex((entity) => entity.name === name);
        if (placeHolderIndex === -1) {
            return;
        }

        this.placeholderEntities[parentIdentity].splice(placeHolderIndex, 1);

        this.requestUpdate();
    }

    // Add a temporary entity with the placeholder state true
    // to allow the UI to show a placeholder during creation.
    createPlaceholderEntity(
        entity: DomainInMenu | ProductInMenu | ProcessInMenu | NewDomain
    ) {
        if (isDomainInMenu(entity)) {
            const placeholderEntity = {
                identity: "",
                name: entity.state.newProductName,
                state: {
                    ...getDefaultMenuState(),
                    placeholder: true,
                },
                domain: entity,
                processes: [],
            } as ProductInMenu;
            this.placeholderEntities[entity.identity] ??= [];
            this.placeholderEntities[entity.identity].push(placeholderEntity);

            this.requestUpdate();
            // Return a callback that can be called to remove the placeholder
            // if it still exists.
            return this.removePlaceholderEntityFn(
                entity,
                entity.state.newProductName || ""
            );
        } else if (isProductInMenu(entity)) {
            const placeholderEntity = {
                identity: "",
                name: entity.state.newProcessName,
                product: entity,
                state: {
                    ...getDefaultMenuState(),
                    placeholder: true,
                },
                userArtefactGroupId: null,
            } as ProcessInMenu;
            this.placeholderEntities[entity.identity] ??= [];
            this.placeholderEntities[entity.identity].push(placeholderEntity);

            this.requestUpdate();
            // Return a callback that can be called to remove the placeholder
            // if it still exists.
            return this.removePlaceholderEntityFn(
                entity,
                entity.state.newProcessName || ""
            );
        } else if (!isProcessInMenu(entity)) {
            const placeholderEntity: DomainInMenu = {
                identity: "",
                name: this.newDomainName,
                state: {
                    ...getDefaultMenuState(),
                    placeholder: true,
                },
                products: [],
            };
            this.placeholderEntities[this.stekId] ??= [];
            this.placeholderEntities[this.stekId].push(placeholderEntity);
            this.requestUpdate();

            // Return a callback that can be called to remove the placeholder
            // if it still exists.
            return this.removePlaceholderEntityFn(entity, this.newDomainName);
        }
    }

    async createEntity(entity: NewDomain | ProductInMenu | ProcessInMenu) {
        const removePlaceholder = this.createPlaceholderEntity({ ...entity });
        let promise: Promise<Domain | Product | Process | undefined>;
        if (isDomainInMenu(entity)) {
            if (!entity.state?.newProductName) {
                return;
            }
            promise = useProductsStore().create(
                entity.state.newProductName,
                entity.identity
            );
        } else if (isProductInMenu(entity)) {
            if (!entity.state?.newProcessName) {
                return;
            }
            promise = useProcessesStore().create(
                entity.state.newProcessName,
                entity.identity
            );
        } else {
            // When the entity is none of the above, we're creating a new root-level
            // domain.
            promise = useDomainsStore().create(entity.name);
            // Clear the domain name, otherwise if someone starts creating a
            // new domain again, the old name will still be there.
            this.newDomainName = "";
        }

        const parentIdentity =
            "identity" in entity ? entity.identity : this.stekId;

        const unwatch = watch(this.manager.worktree, (value) => {
            if (removePlaceholder && value) {
                removePlaceholder(value);
            }
        });

        try {
            const result = await promise;
            if (result) {
                // refreshTree() synchronously rebuilds this.domains after the
                // fetch. Calling store.sync() alone leaves navigation racing
                // the watcher that copies the Pinia worktree into this menu.
                await this.refreshTree();
                if (isDomainInMenu(entity)) {
                    const { product } = this.find({
                        productId: result.identity,
                    });
                    if (product) {
                        this.navigate(product);
                    }
                } else if (isProductInMenu(entity)) {
                    const { process } = this.find({
                        processId: result.identity,
                    });
                    if (process) {
                        this.navigate(process);
                    }
                } else {
                    const { domain } = this.find({ domainId: result.identity });
                    if (domain) {
                        this.navigate(domain);
                    }
                }
            }
        } finally {
            // Always remove the placeholder entity.
            this.removePlaceholderEntity(parentIdentity, entity.name);
            unwatch();
        }
    }

    // Always show the 'create domain' entry.
    createDomainTemplate() {
        if (this.creatingDomain) {
            return html`<li class="node-root domain-root new new-input">
                <div class="entity-name">
                    <input
                        placeholder="Name your domain"
                        type="text"
                        value=${this.newDomainName}
                        @blur=${() => {
                            this.creatingDomain = false;
                            this.newDomainName = "";
                            this.requestUpdate();
                        }}
                        @keydown=${this.onKeydownForCreationEvent({
                            name: this.newDomainName,
                        })}
                        @input=${(e: InputEvent) => {
                            this.newDomainName = (
                                e.target as HTMLInputElement
                            ).value;
                        }}
                    />
                </div>
            </li>`;
        }

        return html`<li class="node-root domain-root new">
            <div
                class="entity-name new-entity"
                @click=${async (e: MouseEvent) => {
                    e.stopPropagation();
                    this.creatingDomain = true;

                    // Wait until Lit is done updating the DOM before trying
                    // to access the input field.
                    this.requestUpdate();

                    await this.updateComplete;

                    const inputField = this.renderRoot.querySelector("input");
                    // Focus the input field.
                    inputField?.focus();
                }}
            >
                New domain
            </div>
        </li>`;
    }

    domainNodeTemplate(domain: DomainInMenu) {
        // Note, forcing a conversion with 'as' to avoid expensive type checks.
        const products =
            domain.identity in this.placeholderEntities
                ? ([
                      ...domain.products,
                      ...this.placeholderEntities[domain.identity],
                  ] as ProductInMenu[])
                : domain.products;
        products.sort(
            (a, b) =>
                (a.order_index ?? 0) - (b.order_index ?? 0) ||
                a.name.localeCompare(b.name)
        );

        const liClasses = {
            ...diffStatusClasses(domain),
            "node-root": true,
            "domain-root": true,
            selected: domain.state.selected,
            open: domain.state.open,
            closed: !domain.state.open,
            draggable: this.editing,
            "drop-target":
                this.dragInfo?.reorderPosition === undefined &&
                domain.identity === this.dragInfo?.dropTarget?.identity,
            "drop-before":
                this.dragInfo?.reorderPosition === "before" &&
                domain.identity === this.dragInfo?.dropTarget?.identity,
            "drop-after":
                this.dragInfo?.reorderPosition === "after" &&
                domain.identity === this.dragInfo?.dropTarget?.identity,
        };

        const divClasses = {
            "entity-name": true,
            placeholder: domain.state.placeholder || false,
        };

        return html`<li
            draggable=${this.editing ? "true" : "false"}
            @dragstart=${(e: DragEvent) => this.onDragStart(e, domain)}
            @dragend=${(e: DragEvent) => this.onDragEnd(e, domain)}
            @dragover=${(e: DragEvent) => this.onDragOver(e, domain)}
            @dragenter=${(e: DragEvent) => this.onDragEnter(e, domain)}
            @dragleave=${(e: DragEvent) => this.onDragLeave(e, domain)}
            @drop=${(e: DragEvent) => this.onDrop(e, domain)}
            class=${classMap(liClasses)}
            @click=${this.onClickToOpenEntityEvent(domain)}
            @contextmenu=${this.onContextMenuEvent(domain)}
        >
            <div class="entity-title" draggable="false">
                <span
                    class="expand-icon ${
                        products.length == 0 && !this.editing ? "empty" : ""
                    }"
                ></span>
                <a
                    class=${classMap(divClasses)}
                    title=${domain.name}
                    @click=${this.onEntityNameClickEvent(domain)}
                    href=${this.getUrl(domain)}
                >
                    ${this.entityNameTemplate(domain)}
                </a>
                ${this.threadBadgeTemplate(domain)}
                ${this.triageBadgeTemplate(domain, "rollup")}
                <span draggable="false" class="drag-icon"></span>
            </div>
            <ul>
                ${products.map((product) =>
                    this.productNodeTemplate(domain, product)
                )}
                ${this.editing ? this.createProductTemplate(domain) : nothing}
            </ul>
        </li>`;
    }

    onEntityNameClickEvent(
        entity: DomainInMenu | ProductInMenu | ProcessInMenu
    ) {
        return (e: MouseEvent) => {
            e.stopPropagation();
            // When the user holds ctrl while clicking the link, don't prevent
            // the default behavior, as this will open the link in a new tab.
            if (e.ctrlKey) {
                return;
            } else {
                e.preventDefault();
            }

            // Do nothing when showing a placeholder.
            if (entity.state.placeholder) {
                return;
            }

            // Do nothing when the entity is being renamed.
            if (entity.state.renaming) {
                return;
            }

            // When the entity is already selected, open the menu, except
            // when the entity is a process.
            if (entity.state.selected && !isProcessInMenu(entity)) {
                this.openOrCloseEntity(entity, true);
            }

            this.navigate(entity);
            this.requestUpdate();
        };
    }

    onClickToOpenEntityEvent(entity: DomainInMenu | ProductInMenu) {
        // When showing a placeholder, don't allow any click events.
        if (entity.state.placeholder) {
            return null;
        }

        return (e: MouseEvent) => {
            e.stopPropagation();

            // Do nothing when in the process of creating a child entity.
            if (entity.state.creating) {
                return;
            }

            this.openOrCloseEntity(entity, !entity.state.open);
            this.requestUpdate();
        };
    }

    productNodeTemplate(domain: DomainInMenu, product: ProductInMenu) {
        // Note, forcing a conversion with 'as' to avoid expensive type checks.
        const processes =
            product.identity in this.placeholderEntities
                ? ([
                      ...product.processes,
                      ...this.placeholderEntities[product.identity],
                  ] as ProcessInMenu[])
                : product.processes;
        processes.sort(
            (a, b) =>
                (a.order_index ?? 0) - (b.order_index ?? 0) ||
                a.name.localeCompare(b.name)
        );

        const liClasses = {
            ...diffStatusClasses(product),
            "node-root": true,
            "product-root": true,
            selected: product.state?.selected,
            open: product.state.open,
            closed: !product.state.open,
            renaming: product.state.renaming,
            draggable: this.editing,
            "drop-target":
                this.dragInfo?.reorderPosition === undefined &&
                product.identity === this.dragInfo?.dropTarget?.identity,
            "drop-before":
                this.dragInfo?.reorderPosition === "before" &&
                product.identity === this.dragInfo?.dropTarget?.identity,
            "drop-after":
                this.dragInfo?.reorderPosition === "after" &&
                product.identity === this.dragInfo?.dropTarget?.identity,
        };

        const divClasses = {
            "entity-name": true,
            placeholder: product.state.placeholder || false,
        };

        return html`<li
            draggable=${this.editing ? "true" : "false"}
            @dragstart=${(e: DragEvent) => this.onDragStart(e, product)}
            @dragend=${(e: DragEvent) => this.onDragEnd(e, product)}
            @dragover=${(e: DragEvent) => this.onDragOver(e, product)}
            @dragenter=${(e: DragEvent) => this.onDragEnter(e, product)}
            @dragleave=${(e: DragEvent) => this.onDragLeave(e, product)}
            @drop=${(e: DragEvent) => this.onDrop(e, product)}
            class=${classMap(liClasses)}
            @click=${this.onClickToOpenEntityEvent(product)}
            @contextmenu=${this.onContextMenuEvent(product)}
        >
            <div class="entity-title" draggable="false">
                <span
                    draggable="false"
                    class="expand-icon ${
                        processes.length == 0 && !this.editing ? "empty" : ""
                    }"
                ></span>
                <a
                    draggable="false"
                    class=${classMap(divClasses)}
                    title=${product.name}
                    @click=${this.onEntityNameClickEvent(product)}
                    href=${this.getUrl(product)}
                >
                    ${this.entityNameTemplate(product)}
                </a>
                ${this.threadBadgeTemplate(product)}
                ${this.triageBadgeTemplate(product, "rollup")}
                <span draggable="false" class="drag-icon"></span>
            </div>
            <ul draggable="false">
                ${processes.map((process) =>
                    this.processNodeTemplate(domain, product, process)
                )}
                ${
                    this.editing
                        ? this.createProcessTemplate(domain, product)
                        : nothing
                }
            </ul>
        </li>`;
    }

    entityNameTemplate(entity: DomainInMenu | ProductInMenu | ProcessInMenu) {
        if (!entity.state.renaming) {
            return html`${entity.name}`;
        }

        return html`<input
            placeholder=${entity.state.newName}
            type="text"
            value=${entity.state.newName}
            @blur=${() => {
                entity.state.renaming = false;
                entity.state.newName = "";
                this.requestUpdate();
            }}
            @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Escape") {
                    entity.state.renaming = false;
                    entity.state.newName = "";
                    this.requestUpdate();
                } else if (e.key === "Enter") {
                    this.renameEntity(entity);
                }
            }}
            @input=${(e: InputEvent) => {
                entity.state.newName = (e.target as HTMLInputElement).value;
            }}
        />`;
    }

    getDeleteEventDetail(entity: DomainInMenu | ProductInMenu | ProcessInMenu) {
        const detail = {
            name: entity.name,
            identity: entity.identity,
        } as {
            name: string;
            identity: string;
            entityType: "domain" | "product" | "process";
            domainName: string;
            productName?: string;
            processName?: string;
        };
        if (isDomainInMenu(entity)) {
            detail["entityType"] = "domain";
            detail["domainName"] = entity.name;
        } else if (isProductInMenu(entity)) {
            detail["entityType"] = "product";
            detail["domainName"] = entity.domain.name;
            detail["productName"] = entity.name;
        } else if (isProcessInMenu(entity)) {
            detail["entityType"] = "process";
            detail["domainName"] = entity.product.domain.name;
            detail["productName"] = entity.product.name;
            detail["processName"] = entity.name;
        }
        return detail;
    }

    getUrl(
        entity:
            | (NewDomain & { identity: string })
            | DomainInMenu
            | ProductInMenu
            | ProcessInMenu
    ) {
        if (!entity.identity) {
            return undefined;
        }
        const { organization } = useOrganizationStore();
        const { stek } = useStekManager();
        const isLive = this.mode === "live";
        const baseParams = {
            orgName: organization?.name,
            stekName: stek.value?.name,
        };
        let location: RouteLocationRaw;
        if (isProcessInMenu(entity)) {
            location = {
                name: isLive ? "liveProcessPage" : "processPage",
                params: { ...baseParams, processId: entity.identity },
            };
        } else if (isProductInMenu(entity)) {
            location = {
                name: isLive ? "liveProductPage" : "productPage",
                params: { ...baseParams, productId: entity.identity },
            };
        } else {
            // Assume domain
            location = {
                name: isLive ? "liveDomainPage" : "domainPage",
                params: { ...baseParams, domainId: entity.identity },
            };
        }
        // Keep design-mode links honest while a thread branch is open: render the
        // ?thread= param straight into the href (no-op in live mode / working copy).
        if (!isLive) location = withThreadRevision(location);
        return router.resolve(location).fullPath;
    }

    navigate(entity: DomainInMenu | ProductInMenu | ProcessInMenu) {
        const url = this.getUrl(entity);
        this.selectEntity(entity);
        if (url) {
            router.push(url);
        }
    }

    onContextMenuEvent(entity: DomainInMenu | ProductInMenu | ProcessInMenu) {
        return (e: MouseEvent) => {
            if (!this.editing) {
                return;
            }
            // See https://gitlab.com/stekz/mystekz/mystekz/-/issues/130#note_2099795679
            document.getElementById("sidebar")?.classList.add("on-top");
            e.preventDefault();
            e.stopPropagation();
            // The overlay is viewport-sized, so use the context-menu pointer's
            // viewport coordinates rather than coordinates relative to the tree.
            const position = positionContextMenu(
                { x: e.clientX, y: e.clientY },
                { width: window.innerWidth, height: window.innerHeight },
                { width: 160, height: 80 }
            );
            this.contextMenu = { entity, ...position };
            this.requestUpdate();
        };
    }

    async renameEntity(entity: DomainInMenu | ProductInMenu | ProcessInMenu) {
        if (!entity || !entity.state) {
            return;
        }

        if (entity.name === entity.state.newName || !entity.state.newName) {
            entity.state.renaming = false;
            this.requestUpdate();
            return;
        }

        this.dispatchEvent(
            new CustomEvent("renaming", {
                composed: true,
                bubbles: true,
                detail: true,
            })
        );

        if (isDomainInMenu(entity)) {
            const { domain } = useDomainManager();
            if (!domain.value) {
                return;
            }
            domain.value.name = entity.state.newName;
        } else if (isProductInMenu(entity)) {
            const { product } = useProductManager();
            if (!product.value) {
                return;
            }
            product.value.name = entity.state.newName;
        } else if (isProcessInMenu(entity)) {
            const { process } = useProcessManager();
            if (!process.value) {
                return;
            }
            process.value.name = entity.state.newName;
        } else {
            throw new Error("Unknown entity type");
        }

        // Assume the new name;
        entity.state.renaming = false;
        entity.name = entity.state.newName;
        this.requestUpdate();
    }

    processNodeTemplate(
        _domain: DomainInMenu,
        _product: ProductInMenu,
        process: ProcessInMenu
    ) {
        const liClasses = {
            ...diffStatusClasses(process),
            "node-root": true,
            "process-root": true,
            selected: process.state.selected,
            open: process.state.open,
            closed: !process.state.open,
            draggable: this.editing,
            "drop-before":
                this.dragInfo?.reorderPosition === "before" &&
                process.identity === this.dragInfo?.dropTarget?.identity,
            "drop-after":
                this.dragInfo?.reorderPosition === "after" &&
                process.identity === this.dragInfo?.dropTarget?.identity,
        };

        const divClasses = {
            "entity-name": true,
            placeholder: process.state.placeholder || false,
            renaming: process.state.renaming,
        };

        return html`<li
            draggable=${this.editing ? "true" : "false"}
            @dragstart=${(e: DragEvent) => this.onDragStart(e, process)}
            @dragend=${(e: DragEvent) => this.onDragEnd(e, process)}
            @dragover=${(e: DragEvent) => this.onDragOver(e, process)}
            @dragenter=${(e: DragEvent) => this.onDragEnter(e, process)}
            @dragleave=${(e: DragEvent) => this.onDragLeave(e, process)}
            @drop=${(e: DragEvent) => this.onDrop(e, process)}
            @mouseover=${(e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            class=${classMap(liClasses)}
            @contextmenu=${this.onContextMenuEvent(process)}
        >
            <div class="entity-title" draggable="false">
                <a
                    draggable="false"
                    class=${classMap(divClasses)}
                    title=${process.name}
                    @click=${this.onEntityNameClickEvent(process)}
                    href=${this.getUrl(process)}
                >
                    ${this.entityNameTemplate(process)}
                </a>
                ${this.threadBadgeTemplate(process)}
                ${this.triageBadgeTemplate(process, "direct")}
                <span draggable="false" class="drag-icon"></span>
            </div>
        </li>`;
    }

    openOrCloseEntity(
        entity: DomainInMenu | ProductInMenu | ProcessInMenu,
        open: boolean
    ) {
        let hasChildren = false;
        let isCreating = false;
        if (isDomainInMenu(entity)) {
            // In edit mode, the "New product" button is always rendered inside
            // the domain's child list, so treat the domain as having children.
            hasChildren = entity.products.length > 0 || this.editing;
            isCreating = entity.state.creating || false;
        } else if (isProductInMenu(entity)) {
            // In edit mode, the "New process" button is always rendered inside
            // the product's child list, so treat the product as having children.
            hasChildren = entity.processes.length > 0 || this.editing;
            isCreating = entity.state.creating || false;
        }

        // Always close or keep the entity closed when it has no children, unless we're creating a new child.
        if (!hasChildren && !isCreating) {
            entity.state.open = false;
            return;
        }

        entity.state.open = open;
    }

    selectEntity(entity: DomainInMenu | ProductInMenu | ProcessInMenu) {
        this.deselectOthers(entity.identity);
        this.openOrCloseEntity(entity, true);
        entity.state.selected = true;
        this.requestUpdate();
    }

    find({
        domainId,
        productId,
        processId,
    }: {
        domainId?: string;
        productId?: string;
        processId?: string;
    }) {
        let domain: DomainInMenu | undefined = undefined;
        let product: ProductInMenu | undefined = undefined;
        let process: ProcessInMenu | undefined = undefined;

        // Note, we're not using the search functions from the worktree
        // manager as those will search in a different tree than the one we're
        // using in this component.
        if (processId) {
            for (const d of this.domains) {
                for (const p of d.products) {
                    for (const pr of p.processes) {
                        if (pr.identity === processId) {
                            domain = d;
                            product = p;
                            process = pr;
                            break;
                        }
                    }
                }
            }
        } else if (productId) {
            for (const d of this.domains) {
                for (const p of d.products) {
                    if (p.identity === productId) {
                        domain = d;
                        product = p;
                        break;
                    }
                }
            }
        } else if (domainId) {
            domain = this.domains.find(
                (domain: DomainInTree) => domain.identity === domainId
            );
        }
        return { domain, product, process };
    }

    // Find the most-nested node which matches the given domain, product and
    // process name and select it. Also open any nodes for parents of the
    // selected node along the way.
    async select(domainId: string, productId: string, processId: string) {
        if (!domainId && !productId && !processId) {
            this.deselectAll();
            return;
        }

        const { domain, product, process } = this.find({
            domainId,
            productId,
            processId,
        });

        if (process && product && domain) {
            // If a nested process is selected, also open the parents.
            domain.state.open = true;
            product.state.open = true;
            this.selectEntity(process);
        } else if (product && domain) {
            // If a nested product is selected, also open the parent domain.
            domain.state.open = true;
            this.selectEntity(product);
        } else if (domain) {
            if (!domain) {
                this.deselectAll();
                return;
            }
            this.selectEntity(domain);
            return;
        }
    }

    // Depending on the type of entity, we show different entity creation options.
    contextMenuCreateNodeTemplate() {
        return nothing;
    }

    contextMenuRenameNodeTemplate() {
        if (!this.contextMenu) {
            return nothing;
        }
        return html` <li
            @click=${async () => {
                // Navigate to the item, since we can currently only update
                // the currently active entity.
                this.navigate(this.contextMenu!.entity);
                // Start with the name of the entity as the new name.
                this.contextMenu!.entity.state.newName =
                    this.contextMenu!.entity.name;
                this.contextMenu!.entity.state.renaming = true;
                document.getElementById("sidebar")?.classList.remove("on-top");
                this.contextMenu = null;

                // Wait until Lit is done updating the DOM before trying
                // to access the input field.
                await this.updateComplete;

                const inputField = this.renderRoot.querySelector("input");
                if (inputField) {
                    // Focus the input field.
                    inputField.focus();
                    // A trick to make the cursor move to the end of the input field
                    inputField.selectionStart = 1000;
                    inputField.selectionEnd = 1000;
                }
            }}
        >
            Rename
        </li>`;
    }

    contextMenuTemplate() {
        if (this.contextMenu === null || !this.editing) {
            return;
        }

        // Clicking anywhere on the background removes the context menu
        // background again.
        return html`<div
            class="context-menu-bg"
            @click=${() => {
                // See: https://gitlab.com/stekz/mystekz/mystekz/-/issues/130#note_2099795679
                document.getElementById("sidebar")?.classList.remove("on-top");
                this.contextMenu = null;
            }}
            @contextmenu=${(e: MouseEvent) => {
                // When right-clicking again while a context menu is open,
                // just close the context menu.
                e.preventDefault();
                document.getElementById("sidebar")?.classList.remove("on-top");
                this.contextMenu = null;
            }}
        >
            <menu
                style="
                  position: absolute;
                  top: ${this.contextMenu.y}px;
                  left: ${this.contextMenu.x}px;
                "
            >
                ${this.contextMenuCreateNodeTemplate()}
                ${this.contextMenuRenameNodeTemplate()}
                <li
                    @click=${() => {
                        this.dispatchEvent(
                            new CustomEvent("delete", {
                                composed: true,
                                bubbles: true,
                                detail: this.getDeleteEventDetail(
                                    this.contextMenu!.entity
                                ),
                            })
                        );
                    }}
                >
                    Delete
                </li>
            </menu>
        </div>`;
    }

    // Discussion badge: an entity touched by an open thread shows a clickable
    // badge that opens the thread drawer at that thread, so users can jump into
    // the relevant discussion. Hidden while viewing a thread branch / release
    // (you're already in a thread context, and the tree shows diff indicators).
    threadBadgeTemplate(entity: DomainInMenu | ProductInMenu | ProcessInMenu) {
        if (!this.userStore.threadsEnabled) return nothing;
        // Hidden while viewing a thread branch or release: pointers INTO
        // threads are noise when you are already inside one.
        if (this.releaseStore.activeRevision) return nothing;
        if (entity.state.placeholder || !entity.identity) return nothing;
        const threads = this.threadsStore.getThreadsForEntity(entity.identity);
        if (!threads.length) return nothing;
        const label =
            threads.length === 1
                ? threads[0].title
                : `${threads.length} discussions`;
        return html`<span
            class="thread-badge"
            title=${label}
            @click=${(e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                this.threadsStore.openThreadsList();
            }}
        >
            <span class="thread-badge-icon"></span>
            ${
                threads.length > 1
                    ? html`<span class="thread-badge-count"
                          >${threads.length}</span
                      >`
                    : nothing
            }
        </span>`;
    }

    // Improvement badge: while the Improvements layer is on, an entity with
    // open triage issues shows a sparkle; domains/products roll up the
    // DISTINCT issues in their subtree (muted). Click opens the triage page
    // scoped to the entity, staying on the branch when a thread is viewed —
    // issues derive at the viewed revision and fixing starts from a thread.
    // Only releases (frozen snapshots) hide the badge.
    triageBadgeTemplate(
        entity: DomainInMenu | ProductInMenu | ProcessInMenu,
        variant: "direct" | "rollup"
    ) {
        if (!this.userStore.externalCommunicationEnabled) return nothing;
        if (!layerVisibility.value.triage) return nothing;
        if (
            this.releaseStore.activeRevision &&
            !this.releaseStore.viewingThreadBranch
        )
            return nothing;
        if (entity.state.placeholder || !entity.identity) return nothing;
        const count =
            variant === "direct"
                ? this.triageStore.issueCountForEntity(entity.identity)
                : this.triageStore.rollupCountForEntity(entity.identity);
        if (!count) return nothing;
        const label =
            variant === "direct"
                ? count === 1
                    ? "1 way to improve this process"
                    : `${count} ways to improve this process`
                : count === 1
                  ? "1 improvement suggestion in here"
                  : `${count} improvement suggestions in here`;
        return html`<span
            class="triage-badge ${variant === "rollup" ? "rollup" : ""}"
            title=${label}
            @click=${(e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                const { organization } = useOrganizationStore();
                const { stek } = useStekManager();
                router.push(
                    withThreadRevision({
                        name: "triage",
                        params: {
                            orgName: organization?.name,
                            stekName: stek.value?.name,
                        },
                        query: { entity: entity.identity },
                    })
                );
            }}
        >
            <span class="triage-badge-icon"></span>
            ${
                variant === "rollup" || count > 1
                    ? html`<span class="triage-badge-count">${count}</span>`
                    : nothing
            }
        </span>`;
    }

    render() {
        if (this.loading) {
            return html`<ul class="file-list root">
                <li class="node-root domain-root closed">
                    <div class="entity-name">Loading..</div>
                </li>
            </ul>`;
        }

        // Note, forcing a conversion with 'as' to avoid expensive type checks.
        const domains =
            this.stekId in this.placeholderEntities
                ? ([
                      ...this.placeholderEntities[this.stekId],
                      ...this.domains,
                  ] as DomainInMenu[])
                : this.domains;
        domains.sort(
            (a, b) =>
                (a.order_index ?? 0) - (b.order_index ?? 0) ||
                a.name.localeCompare(b.name)
        );

        return html`<div>
            ${this.contextMenuTemplate()}
            <ul class="file-list root">
                ${domains.map((domain) => this.domainNodeTemplate(domain))}
                ${this.editing ? this.createDomainTemplate() : nothing}
            </ul>
        </div>`;
    }

    async onDragStart(
        dragEvent: DragEvent,
        entity: ProcessInMenu | ProductInMenu
    ) {
        dragEvent.stopPropagation();
        dragEvent.dataTransfer!.effectAllowed = "move";

        this.dragInfo = { entity: entity, dropSuccess: false };

        const target = dragEvent.target as HTMLElement;

        target.classList.add("dragging");
    }

    async onDragEnd(dragEvent: DragEvent, _: ProcessInMenu | ProductInMenu) {
        const target = dragEvent.target as HTMLElement;

        // Wait for 1s for the drop effect to be processed.
        // If it was not dropped in a valid target, we will remove the dragging class.
        setTimeout(() => {
            if (!this.dragInfo?.dropSuccess) {
                // If the drop effect is not 'move', it means the item was not dropped
                // in a valid drop target, so we need to restore the element.
                target.classList.remove("dragging");
            }
            this.updateMenu();
        }, 500);
    }
    async onDragOver(
        dragEvent: DragEvent,
        target: DomainInMenu | ProductInMenu | ProcessInMenu
    ) {
        dragEvent.preventDefault();
        dragEvent.stopPropagation();

        if (!this.dragInfo || !this.dragInfo.entity) {
            return;
        }

        const entity = this.dragInfo.entity;

        // Determine if we are hovering the entity itself, a sibling, or a descendant of a sibling.
        let reorderTarget:
            DomainInMenu | ProductInMenu | ProcessInMenu | undefined =
            undefined;
        let forceAfter = false;

        if (isDomainInMenu(entity)) {
            if (isDomainInMenu(target)) {
                reorderTarget = target;
            } else if (isProductInMenu(target)) {
                reorderTarget = target.domain;
                forceAfter = true;
            } else if (isProcessInMenu(target)) {
                reorderTarget = target.product.domain;
                forceAfter = true;
            }
        } else if (isProductInMenu(entity)) {
            if (
                isProductInMenu(target) &&
                target.domain?.identity === entity.domain?.identity
            ) {
                reorderTarget = target;
            } else if (
                isProcessInMenu(target) &&
                target.product?.domain?.identity === entity.domain?.identity
            ) {
                reorderTarget = target.product;
                forceAfter = true;
            }
        } else if (isProcessInMenu(entity)) {
            if (
                isProcessInMenu(target) &&
                target.product?.identity === entity.product?.identity
            ) {
                reorderTarget = target;
            }
        }

        if (reorderTarget && reorderTarget.identity !== entity.identity) {
            let newPosition: "before" | "after";

            if (forceAfter) {
                newPosition = "after";
            } else {
                const currentTarget = dragEvent.currentTarget as HTMLElement;
                const titleEl = currentTarget.querySelector(".entity-title");
                const rect = titleEl
                    ? titleEl.getBoundingClientRect()
                    : currentTarget.getBoundingClientRect();

                const midpoint = rect.top + rect.height / 2;
                newPosition = dragEvent.clientY < midpoint ? "before" : "after";
            }

            if (
                this.dragInfo.reorderPosition !== newPosition ||
                this.dragInfo.dropTarget !== reorderTarget
            ) {
                this.dragInfo.reorderPosition = newPosition;
                this.dragInfo.dropTarget = reorderTarget;
                this.requestUpdate();
            }
            return;
        }

        // If it's not a sibling or descendant, it might be a drop target for moving into a parent
        // but we already set the dropTarget in onDragEnter.
        if (this.dragInfo.reorderPosition !== undefined) {
            this.dragInfo.reorderPosition = undefined;
            this.requestUpdate();
        }
    }

    async onDragEnter(
        dragEvent: DragEvent,
        target: DomainInMenu | ProductInMenu | ProcessInMenu
    ) {
        dragEvent.preventDefault();
        dragEvent.stopPropagation();

        if (!this.dragInfo || !this.dragInfo.entity) {
            return;
        }

        const entity = this.dragInfo.entity;
        let isDropTarget = false;
        let isParentOfDropTarget = false;

        // Check if target and entity are siblings for reordering
        const areSiblings =
            (isDomainInMenu(target) && isDomainInMenu(entity)) ||
            (isProductInMenu(target) &&
                isProductInMenu(entity) &&
                target.domain?.identity === entity.domain?.identity) ||
            (isProcessInMenu(target) &&
                isProcessInMenu(entity) &&
                target.product?.identity === entity.product?.identity);

        if (areSiblings && target.identity !== entity.identity) {
            // Reordering will be handled by onDragOver
            return;
        }

        if (isDomainInMenu(target)) {
            if (isProductInMenu(this.dragInfo.entity)) {
                isDropTarget = true;
            } else if (isProcessInMenu(this.dragInfo.entity)) {
                isParentOfDropTarget = true;
            }
        } else if (isProductInMenu(target)) {
            if (isProcessInMenu(this.dragInfo.entity)) {
                isDropTarget = true;
            }
        }

        if (isParentOfDropTarget) {
            const expandTimeoutId = setTimeout(() => {
                // Open the entity after a short delay
                this.openOrCloseEntity(target, true);
                this.requestUpdate();
            }, 1000);

            target.state.expandTimeoutId = expandTimeoutId;
        } else if (isDropTarget) {
            this.dragInfo.dropTarget = target;
            this.dragInfo.reorderPosition = undefined;
            this.requestUpdate();
        }
    }

    async onDragLeave(
        dragEvent: DragEvent,
        target: DomainInMenu | ProductInMenu | ProcessInMenu
    ) {
        dragEvent.preventDefault();
        dragEvent.stopPropagation();

        if (target.state.expandTimeoutId !== undefined) {
            clearTimeout(target.state.expandTimeoutId);
            target.state.expandTimeoutId = undefined;
        }

        if (this.dragInfo?.dropTarget === target) {
            this.dragInfo.dropTarget = undefined;
            this.dragInfo.reorderPosition = undefined;
            this.requestUpdate();
        }
    }

    async onDrop(
        dragEvent: DragEvent,
        target: DomainInMenu | ProductInMenu | ProcessInMenu
    ) {
        dragEvent.preventDefault();

        if (!this.dragInfo || !this.dragInfo.entity) {
            return;
        }
        const entity = this.dragInfo.entity;

        if (this.dragInfo.reorderPosition) {
            let siblings: (DomainInMenu | ProductInMenu | ProcessInMenu)[] = [];
            let entityType: EntityType = EntityType.DOMAIN;

            if (isDomainInMenu(entity)) {
                siblings = this.domains;
                entityType = EntityType.DOMAIN;
            } else if (isProductInMenu(entity)) {
                siblings = entity.domain.products;
                entityType = EntityType.PRODUCT;
            } else if (isProcessInMenu(entity)) {
                siblings = entity.product.processes;
                entityType = EntityType.PROCESS;
            }

            const newOrder = [...siblings].filter(
                (s) => s.identity !== entity.identity
            );
            const targetIndex = newOrder.findIndex(
                (s) => s.identity === target.identity
            );

            if (this.dragInfo.reorderPosition === "before") {
                newOrder.splice(targetIndex, 0, entity);
            } else {
                newOrder.splice(targetIndex + 1, 0, entity);
            }

            // Optimistic update: assign new order indices to the local entities
            // so the UI reflects the new order immediately.
            newOrder.forEach((s, index) => {
                s.order_index = index;
            });

            this.dispatchEvent(
                new CustomEvent("reorder", {
                    composed: true,
                    bubbles: true,
                    detail: {
                        entityType,
                        ids: newOrder.map((s) => s.identity),
                    },
                })
            );
            this.dragInfo.dropSuccess = true;
        } else if (isProductInMenu(target) && isProcessInMenu(entity)) {
            if (target.identity === entity.product?.identity) {
                // Don't allow moving a process to the same product.
                console.warn("Process is already in the target product.");
            } else {
                this.dispatchEvent(
                    new CustomEvent("move", {
                        composed: true,
                        bubbles: true,
                        detail: {
                            entityType: EntityType.PROCESS,
                            entity: entity,
                            target: target,
                        },
                    })
                );
                this.dragInfo.dropSuccess = true;
            }
        } else if (isDomainInMenu(target) && isProductInMenu(entity)) {
            if (target.identity === entity.domain?.identity) {
                // Don't allow moving a product to the same domain.
                console.warn("Product is already in the target domain.");
            } else {
                this.dispatchEvent(
                    new CustomEvent("move", {
                        composed: true,
                        bubbles: true,
                        detail: {
                            entityType: EntityType.PRODUCT,
                            entity: entity,
                            target: target,
                        },
                    })
                );
                this.dragInfo.dropSuccess = true;
            }
        } else {
            // The drop was incorrect, so we don't allow the drop.
        }

        this.renderRoot
            .querySelectorAll("li.node-root.dragging")
            .forEach((element) => element.classList.remove("dragging"));

        if (this.dragInfo.dropSuccess) {
            this.dragInfo = null;
            this.requestUpdate();
        }
    }
}
